import React, {useContext} from 'react';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import * as d3 from 'd3';
import styled from 'styled-components';

import {compactNumber, formatNumberWithSymbol} from '../../services/helpers';
import Colors from "../../assets/colors/Colors";
import {RankingsAndLevelsContext} from "../../contexts/RankingsAndLevelsContext";

interface LineChartPoint {
    x: number,
    y: number
}

interface LineChartProps {
    values: LineChartPoint[],
    suffix?: string
}

interface TimeAxisProps {
    years: number[]
}

interface VerticalAxisProps {
    data: number[],
    suffix?: string
}

const MARGIN = {
    top: 20,
    bottom: 60,
    left: 60,
    right: 10
};

const BOX_WIDTH = 300;
const BOX_HEIGHT = 300;
const CHART_WIDTH = BOX_WIDTH - MARGIN.left - MARGIN.right;
const CHART_HEIGHT = BOX_HEIGHT - MARGIN.top - MARGIN.bottom;

const XAxisTick = styled.g`
  & .grid-line {
    stroke-opacity: .26;
    stroke-width: 1px;
    stroke-linecap: butt;
  }

  &:first-child .grid-line {
    stroke-opacity: 1;
    stroke-width: 2px;
  }

  &:last-child .grid-line {
    stroke-opacity: 1;
    stroke-width: 2px;
  }
`

const TimeAxis = (props: TimeAxisProps) => {
    const yearScale = d3.scaleLinear()
        // @ts-ignore
        .domain(d3.extent(props.years))
        .range([0, CHART_WIDTH]);

    const ticks = yearScale.ticks(props.years.length)
        .filter((tick: number) => Number.isInteger(tick))
        .map((value: number) => ({value, xOffset: yearScale(value)}));

    return (
        <g>
            // @ts-ignore
            {ticks.map(({value, xOffset}) => (
                <XAxisTick key={`${value}-${xOffset}-tick`}>
                    <text
                        key={`${value}-${xOffset}-label`}
                        style={{
                            fontSize: '10px',
                            textAnchor: 'middle',
                            fontFamily: 'DMSans-Bold',
                            transform: `translate(${xOffset + MARGIN.bottom}px, ${20 + BOX_WIDTH - MARGIN.bottom}px)`,
                            stroke: 'none'
                        }}
                        transform={`translate(${xOffset + MARGIN.bottom} ${20 + BOX_WIDTH - MARGIN.bottom})`}
                    >
                        {value}
                    </text>
                    <line
                        key={`${value}-${xOffset}-line`}
                        className='grid-line'
                        x1={xOffset + MARGIN.bottom}
                        x2={xOffset + MARGIN.bottom}
                        y1='0'
                        y2={BOX_WIDTH - MARGIN.bottom}
                    />
                </XAxisTick>
            ))}
        </g>
    )
};


const VerticalAxis = (props: VerticalAxisProps) => {
    const { t, i18n } = useTranslation();
    const lng = i18n.language || window.localStorage.i18nextLng || 'es';

    const dataScale = d3.scaleLinear()
        // @ts-ignore
        .domain(d3.extent(props.data))
        .range([BOX_WIDTH - MARGIN.bottom, MARGIN.top]);

    const ticks = dataScale.nice().ticks(5)
        .map((value: number) => ({value, yOffset: dataScale(value)}));

    return (
        <g>
            // @ts-ignore
            {ticks.map(({value, yOffset}) => (
                <text
                    key={yOffset}
                    style={{
                        fontSize: '10px',
                        textAnchor: 'start',
                        transform: `translate(0px, ${yOffset}px)`,
                        fontFamily: 'DMSans-Light',
                        stroke: 'none'
                    }}
                    transform={`translate(0 ${yOffset})`}
                >
                    {formatNumberWithSymbol(value, 2, lng)['n']} {formatNumberWithSymbol(value, 2, lng)['symbol']} {props.suffix}
                </text>
            ))}
        </g>
    )
};

const DataLine = (props: LineChartProps) => {
    const xValues = props.values.map(value => value.x);
    const yValues = props.values.map(value => value.y);

    const xScale = d3.scaleLinear()
        // @ts-ignore
        .domain(d3.extent(xValues))
        .range([0, CHART_WIDTH]);

    const yScale = d3.scaleLinear()
        // @ts-ignore
        .domain(d3.extent(yValues))
        .range([BOX_HEIGHT - MARGIN.bottom, MARGIN.top]);

    const line = d3.line()
        // @ts-ignore
        .x(d => (xScale(d.x) + MARGIN.left))
        // @ts-ignore
        .y(d => (yScale(d.y)));

    // @ts-ignore
    const svgLine = line(props.values);

    // @ts-ignore
    const { isLife } = useContext(RankingsAndLevelsContext);
    const color = isLife ? Colors.fontWhite : Colors.fontGray;

    return (
        <g>
            <path
                style={{
                    stroke: color,
                    strokeWidth: '6px',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'miter',
                    boxShadow: '0px 6px 2px #00000029',
                    fill: 'none'
                }}
                d={svgLine}
            />
        </g>
    )
};


const LineChart = (props: LineChartProps) => {
    const xValues = props.values.map(value => value.x);
    const yValues = props.values.map(value => value.y);

    // @ts-ignore
    const { isLife } = useContext(RankingsAndLevelsContext);
    return (
        <svg width='100%' viewBox='0 0 300 300'
             fill={isLife ? Colors.fontWhite : Colors.fontGray}
             stroke={isLife ? Colors.fontWhite : Colors.fontGray}>
            <VerticalAxis data={ yValues } suffix={ props.suffix }/>
            <TimeAxis years={ xValues }/>
            <DataLine {...props} />
        </svg>
    )
};


export default LineChart;
